import { useState } from "react";
import { Box, styled } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

import AuthenticateLayout from "../../components/AuthenticateLayout";
import ChangeLanguage from "../../components/ChangeLanguage";
import { ENV } from "../../configs/env";
import { SubmitButton } from "./components/styles";

import { links } from "../../constants/links";


const Wrapper = styled(Box)({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "-webkit-fill-available"
});

const TextWrapper = styled(Box)({
    fontSize: 14,
    color: "#0f3a65",
    textAlign: "left",
    lineHeight: 1.7,
    whiteSpace: "pre-line",
    fontFamily: `
    -apple-system,
    BlinkMacSystemFont,
    "Hiragino Kaku Gothic ProN",
    "Hiragino Sans",
    "Noto Sans JP",
    Meiryo,
    sans-serif
  `,
});

type DatePart =
    | "year"
    | "month"
    | "day"
    | "weekday"
    | "hour"
    | "minute"
    | "second";

interface JstDateOptions {
    includeWeekday?: boolean;
    includeDay?: boolean;
    includeTime?: boolean;
    includeSeconds?: boolean;
}



const PaypayCoupon: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const {
        engageSession,
        couponSession,
        couponValue,
        couponCode,
        createDateISO,
        couponExpiredAt,
        couponReference,
        couponExternalCode,
        isPayPayConsentCompleted
    } = location.state || {};

    const [isSubmitting, setIsSubmitting] = useState(false);

    const formatJstDate = (
        dateInput?: string | Date,
        option: JstDateOptions = {}
    ): string | null => {

        if (!dateInput) return null;

        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(date.getTime())) return null;

        const {
            includeWeekday = true,
            includeDay = true,
            includeTime = false,
            includeSeconds = false
        } = option;

        const isJa = i18n.language.startsWith("ja");
        const isEng = i18n.language.startsWith("en");

        const formatOptions: Intl.DateTimeFormatOptions = {
            timeZone: "Asia/Tokyo",
            year: "numeric",
            month: isJa ? "numeric" : "long",
        };

        if (includeDay) formatOptions.day = "numeric";
        if (includeWeekday) formatOptions.weekday = "short";
        if (includeTime) {
            formatOptions.hour = "2-digit";
            formatOptions.minute = "2-digit";
            formatOptions.hour12 = false;
            if (includeSeconds) formatOptions.second = "2-digit";
        }

        const formatter = new Intl.DateTimeFormat(
            isEng ? "en-US" : "ja-JP",
            formatOptions);

        if (!isEng) {
            const parts = formatter.formatToParts(date);
            const mapDate = Object.fromEntries(parts.map(p => [p.type, p.value])) as Record<DatePart, string>;
            let result = `${mapDate.year}年${mapDate.month}月`;
            if (includeDay) result += `${mapDate.day}日`;
            if (includeWeekday && mapDate.weekday) result += `(${mapDate.weekday})`;
            if (includeTime && mapDate.hour && mapDate.minute) {
                result += ` ${mapDate.hour}:${mapDate.minute}`;
                if (includeSeconds && mapDate.second) result += `:${mapDate.second}`;
            }
            return result;
        }
        return formatter.format(date);
    };


    const submitPaypay = async () => {
        if (!couponSession || !engageSession) {
            console.error("coupon session and engageSession are required");
            navigate(`${links.SMART_RETAIL_ERROR}/?error-msg=invalid_request`);
            return;
        }

        try {
            setIsSubmitting(true);

            if (isPayPayConsentCompleted) {
                navigate(
                    `${links.USE_REWARD_V2}/?engage-session=${engageSession}&coupon-session=${couponSession}`,
                    { state: { isFromPaypayCouponPage: true } }
                );
            } else {
                return navigate(
                    `${links.PAY_PAY_CONSENT}`,
                    {
                        state: {
                            engageSession: engageSession,
                            couponSession: couponSession
                        }
                    }
                );
            }
        } catch (err) {
            console.error("Consent failed", err);
            navigate(`${links.SMART_RETAIL_ERROR}/?error-msg=invalid_request`);
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <Wrapper>
            <AuthenticateLayout
                hideBackground={true}
                button={() => !ENV.HIDE_TRANSLATOR && <ChangeLanguage />}
                isLoading={isSubmitting}
                hideLogo={ENV.HIDE_LOGO}
            >
                <Box
                    px={4}
                    mt={8}
                    display="flex"
                    justifyContent="center"
                    flexDirection="column"
                >
                    <SubmitButton
                        // disabled={!checked || isSubmitting}
                        sx={{
                            whiteSpace: "pre-line",
                        }}
                        onClick={submitPaypay}
                    >
                        {t("paypayCoupon.getPayPayBtn")}
                    </SubmitButton>
                    <TextWrapper
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            paddingTop: 3,
                            paddingBottom: 5,
                            whiteSpace: "pre-line"
                        }}
                    >
                        {t("paypayCoupon.body1", {
                            couponValue,
                            couponExternalCode,
                            couponCode,
                            couponReference,
                            validFrom: formatJstDate(createDateISO),
                            validTo: formatJstDate(couponExpiredAt),
                            issueDate: formatJstDate(createDateISO, { includeWeekday: false })
                        })}
                        <Trans
                            i18nKey={"paypayCoupon.body2"}
                            components={{
                                helpLink: (
                                    <a
                                        href="https://paypay.ne.jp/help/c0485/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#1976d2", display: "inline-block" }}
                                    >
                                        https://paypay.ne.jp/help/c0485/
                                    </a>
                                )
                            }}
                        />
                        {t("paypayCoupon.body3")}
                        {/* <Trans
                            i18nKey={"paypayCoupon.body4"}
                            components={{
                                mail: (
                                    <a href="mailto:rm-support@lmig.co.jp" style={{ color: "#1976d2" }} >rm-support@lmig.co.jp</a>
                                )
                            }}
                        /> */}
                    </TextWrapper>
                    <Box
                        sx={{
                            paddingTop: 5
                        }}
                    >

                    </Box>
                </Box>
            </AuthenticateLayout>
        </Wrapper>
    );
};

export default PaypayCoupon;

